/**
 * HandoffActionModal Component
 * The one confirmation left in the handoff flow: declining from the
 * notification bell or the dashboard Handoffs modal (SPEC-2300).
 *
 * Everything else acts on the first tap. Decline keeps a dialog because it is
 * the only in-app action that cannot be undone and that no earlier step has
 * already stated: claiming can be given back, and the SMS actions were chosen
 * by tapping a link that named them, so a dialog there would only ask the same
 * question twice.
 *
 * It collects no notes. Notes belong to the live-transfer post-call page, which
 * asks for them inline where the agent is already reading the summary. The
 * simplified lanes collect none at all — an agent declining an offer has
 * nothing to report.
 */

import React from 'react';
import { Button, Modal, Typography } from '@crisellevaldez/speculo-ui';
import { Phone, XCircle } from 'lucide-react';
import { formatPhoneNumber } from '../../../utils/formatters';

interface HandoffActionModalProps {
  isOpen: boolean;
  contactName: string;
  contactPhone: string | null | undefined;
  isSubmitting: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

export const HandoffActionModal: React.FC<HandoffActionModalProps> = ({
  isOpen,
  contactName,
  contactPhone,
  isSubmitting,
  onClose,
  onConfirm,
}) => {
  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="base">
      <Modal.Header>
        <Typography variant="h3">Decline handoff</Typography>
      </Modal.Header>
      <Modal.Body>
        <div className="flex flex-col gap-4">
          <p className="text-sm text-muted-foreground">
            This handoff leaves your queue. Remi carries on working the lead.
          </p>

          <div className="rounded-lg border border-foreground/20 bg-foreground/[0.03] px-4 py-3">
            <p className="text-base font-semibold text-foreground">{contactName}</p>
            {contactPhone && (
              <p className="mt-0.5 inline-flex items-center gap-1.5 text-sm text-muted-foreground">
                <Phone className="h-3.5 w-3.5" />
                {formatPhoneNumber(contactPhone)}
              </p>
            )}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={onConfirm}
            disabled={isSubmitting}
            isLoading={isSubmitting}
            // `variant="primary"` ships text-primary-foreground, which is
            // near-black — legible on the amber primary, not on red. The text
            // colour has to move with the background.
            className="bg-red-600 text-white hover:bg-red-700 dark:bg-red-600 dark:text-white dark:hover:bg-red-500"
          >
            <XCircle className="mr-2 h-4 w-4" />
            Decline
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default HandoffActionModal;
